import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import type { Analytics } from 'firebase/analytics';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
// experimentalForceLongPolling — Safari cannot reliably maintain a WebChannel to
// firestore.googleapis.com (CORS errors on /Listen/channel), so we use long-polling
// unconditionally, skipping WebChannel attempts and the 30-second retry loop.
// DO NOT switch to experimentalAutoDetectLongPolling: it was tried (SCD-694) and
// breaks Safari — the first authed read fails with permission-denied (the
// connection comes up without a working auth channel) and the workspace hangs on
// the loader. Auto-detect's probe doesn't reliably catch Safari's failure mode.
export const db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    // Persist Firestore data in IndexedDB so repeat loads / refreshes serve from cache
    // instantly and sync in the background, instead of fetching everything cold each time.
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const functions = getFunctions(app, 'europe-central2');
export const storage = getStorage(app);

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
}

// Firebase Analytics is loaded lazily (dynamic import) and guarded by isSupported()
// — it ran eagerly at module load before, pulling the analytics SDK on every page
// (including the logged-out landing) and initializing gtag even without a
// measurementId. Now it loads only when an event is actually logged.
let analyticsInstance: Analytics | null = null;
let analyticsInit: Promise<Analytics | null> | null = null;

export async function logAnalyticsEvent(eventName: string, params?: Record<string, unknown>): Promise<void> {
    if (import.meta.env.VITE_USE_EMULATORS === 'true') return;
    try {
        const { getAnalytics, isSupported, logEvent } = await import('firebase/analytics');
        if (!analyticsInstance) {
            if (!analyticsInit) {
                analyticsInit = isSupported().then((ok) => (ok ? getAnalytics(app) : null));
            }
            analyticsInstance = await analyticsInit;
            if (!analyticsInstance) return;
        }
        logEvent(analyticsInstance, eventName, params);
    } catch (e) {
        console.error('[analytics] logEvent failed', e);
    }
}